/**
 * @name DependenteCard
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Card individual para exibição de dados do dependente
 * @package components/pages/gestao-financeira/dependentes/components
 */

import { AccountBalance as AccountBalanceIcon, AttachMoney as AttachMoneyIcon, Person as PersonIcon, Settings as SettingsIcon, SwapHoriz as SwapHorizIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { Avatar, Box, Card, CardContent, Chip, Grid, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { useThemeContext } from '../../../../../contexts';
import ActionButton from '../../../../common/buttons/ActionButton';

interface DependenteCardProps {
     /**
      * Dados do dependente da view vw_saldos_alunos
      */
     dependente: {
          id_aluno: number | null;
          aluno: string | null;
          saldo_disponivel: number | null;
          limite_diario?: number | null;
          limite_semanal?: number | null;
     };

     /**
      * Handler para distribuir saldo
      */
     onDistribuirSaldo: (idAluno: number) => void;

     /**
      * Handler para transferir saldo
      */
     onTransferirSaldo: (idAluno: number) => void;

     /**
      * Handler para configurar dependente
      */
     onConfigurar: (idAluno: number) => void;

     /**
      * Handler para ver detalhes do dependente
      */
     onVerDetalhes?: (idAluno: number) => void;

     /**
      * Estado de loading
      */
     loading?: boolean;
}

/**
 * Card individual para exibição de dados do dependente
 * Consome dados da view vw_saldos_alunos
 */
const DependenteCard: React.FC<DependenteCardProps> = ({ dependente, onDistribuirSaldo, onTransferirSaldo, onConfigurar, onVerDetalhes, loading = false }) => {
     const { theme } = useThemeContext();
     const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

     // Alternativa: detecção por largura da janela
     const isReallyMobile = window.innerWidth <= 600;

     // Vamos usar a detecção que realmente funciona
     const actualMobile = isMobile || isReallyMobile;

     // Função para gerar iniciais do nome
     const getInitials = (nome: string | null): string => {
          if (!nome) return '??';
          return nome
               .split(' ')
               .map((word) => word.charAt(0).toUpperCase())
               .slice(0, 2)
               .join('');
     };

     // Validações de dados
     const saldoDisponivel = dependente.saldo_disponivel ?? 0;
     const nomeAluno = dependente.aluno ?? 'Nome não informado';
     const idAluno = dependente.id_aluno ?? 0;
     const podeComprar = saldoDisponivel > 0;

     // Cor do avatar baseada no status
     const getAvatarColor = () => {
          if (!podeComprar) {
               return theme.palette.error.main;
          }
          if (saldoDisponivel < 5) {
               return theme.palette.warning.main;
          }
          return theme.palette.primary.main;
     };

     // Status do saldo
     const getSaldoStatus = () => {
          if (saldoDisponivel <= 0) {
               return { label: 'Sem Saldo', color: 'error' as const };
          }
          if (saldoDisponivel < 5) {
               return { label: 'Saldo Baixo', color: 'warning' as const };
          }
          return { label: 'Ativo', color: 'success' as const };
     };

     const saldoStatus = getSaldoStatus();

     return (
          <Card
               sx={{
                    position: 'relative',
                    height: '100%',
                    maxHeight: actualMobile ? 380 : 420,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                         transform: 'translateY(-2px)',
                         boxShadow: theme.shadows[4],
                    },
                    opacity: loading ? 0.7 : 1,
                    pointerEvents: loading ? 'none' : 'auto',
               }}
          >
               <CardContent
                    sx={{
                         p: actualMobile ? 2 : 3,
                         height: '100%',
                         display: 'flex',
                         flexDirection: 'column',
                         gap: actualMobile ? 1.5 : 2,
                         overflow: 'hidden',
                    }}
               >
                    {/* Header com Avatar e Nome - SEM sobreposição */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                         <Avatar
                              sx={{
                                   bgcolor: getAvatarColor(),
                                   width: actualMobile ? 40 : 48,
                                   height: actualMobile ? 40 : 48,
                                   mr: actualMobile ? 1.5 : 2,
                                   fontSize: actualMobile ? '1rem' : '1.2rem',
                                   fontWeight: 'bold',
                              }}
                         >
                              {getInitials(nomeAluno)}
                         </Avatar>

                         <Box sx={{ flexGrow: 1, minWidth: 0, mr: actualMobile ? 0 : 1 }}>
                              <Typography
                                   variant={actualMobile ? 'subtitle1' : 'h6'}
                                   sx={{
                                        fontSize: actualMobile ? '0.95rem' : '1.1rem',
                                        fontWeight: 500,
                                        lineHeight: 1.3,
                                        mb: 0.5,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                   }}
                              >
                                   {nomeAluno}
                              </Typography>
                              <Chip
                                   size='small'
                                   label={saldoStatus.label}
                                   color={saldoStatus.color}
                                   sx={{
                                        fontSize: actualMobile ? '0.65rem' : '0.75rem',
                                        height: actualMobile ? 20 : 24,
                                   }}
                              />
                         </Box>
                    </Box>

                    {/* Informações Financeiras */}
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                         <Grid
                              container
                              spacing={actualMobile ? 1 : 1.5}
                              sx={{ flexGrow: 1 }}
                         >
                              <Grid
                                   item
                                   xs={12}
                                   md={6}
                              >
                                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <AccountBalanceIcon
                                             sx={{
                                                  mr: 1,
                                                  color: 'text.secondary',
                                                  fontSize: actualMobile ? '1rem' : '1.1rem',
                                             }}
                                        />
                                        <Typography
                                             variant='body2'
                                             color='text.secondary'
                                             sx={{ fontSize: actualMobile ? '0.8rem' : '0.875rem' }}
                                        >
                                             Saldo Disponível
                                        </Typography>
                                   </Box>
                                   <Typography
                                        variant={actualMobile ? 'h6' : 'h5'}
                                        color={saldoDisponivel > 0 ? 'primary.main' : 'error.main'}
                                        fontWeight='bold'
                                        sx={{
                                             fontSize: actualMobile ? '1.25rem' : '1.5rem',
                                             wordBreak: 'break-word',
                                        }}
                                   >
                                        R$ {saldoDisponivel.toFixed(2)}
                                   </Typography>
                              </Grid>

                              {/* Status de Compra */}
                              <Grid
                                   item
                                   xs={12}
                                   md={6}
                              >
                                   <Box sx={{ display: 'flex', alignItems: 'center', mt: actualMobile ? 0.5 : 1 }}>
                                        <PersonIcon
                                             sx={{
                                                  mr: 1,
                                                  color: podeComprar ? 'success.main' : 'error.main',
                                                  fontSize: actualMobile ? '1rem' : '1.1rem',
                                             }}
                                        />
                                        <Typography
                                             variant='caption'
                                             color={podeComprar ? 'success.main' : 'error.main'}
                                             sx={{ fontSize: actualMobile ? '0.7rem' : '0.75rem' }}
                                        >
                                             {podeComprar ? 'Pode comprar hoje' : 'Não pode comprar hoje'}
                                        </Typography>
                                   </Box>
                              </Grid>
                              <Grid
                                   item
                                   xs={12}
                                   sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}
                              >
                                   {/* Ações desktop - separadas do nome */}
                                   {!actualMobile && (
                                        <Box
                                             sx={{
                                                  display: 'flex',
                                                  gap: 0.5,
                                                  justifyContent: 'center',
                                                  mb: 2,
                                                  flexWrap: 'wrap',
                                             }}
                                        >
                                             {onVerDetalhes && (
                                                  <ActionButton
                                                       title='Ver Detalhes'
                                                       icon={<VisibilityIcon />}
                                                       onClick={() => idAluno && onVerDetalhes(idAluno)}
                                                       disabled={loading || !idAluno}
                                                       color='info'
                                                       size='small'
                                                  />
                                             )}
                                             <ActionButton
                                                  title='Distribuir Saldo'
                                                  icon={<AttachMoneyIcon />}
                                                  onClick={() => idAluno && onDistribuirSaldo(idAluno)}
                                                  disabled={loading || !idAluno}
                                                  color='primary'
                                                  size='small'
                                             />
                                             <ActionButton
                                                  title='Configurações'
                                                  icon={<SettingsIcon />}
                                                  onClick={() => idAluno && onConfigurar(idAluno)}
                                                  disabled={loading || !idAluno}
                                                  size='small'
                                             />
                                        </Box>
                                   )}

                                   {/* Ações móveis - ficam abaixo do header em mobile */}
                                   {actualMobile && (
                                        <Box
                                             sx={{
                                                  display: 'flex',
                                                  gap: 0.5,
                                                  flexWrap: 'wrap',
                                                  justifyContent: 'center',
                                                  mb: 2,
                                             }}
                                        >
                                             {onVerDetalhes && (
                                                  <ActionButton
                                                       title='Ver Detalhes'
                                                       icon={<VisibilityIcon />}
                                                       onClick={() => idAluno && onVerDetalhes(idAluno)}
                                                       disabled={loading || !idAluno}
                                                       color='info'
                                                       size='small'
                                                  />
                                             )}
                                             <ActionButton
                                                  title='Distribuir Saldo'
                                                  icon={<AttachMoneyIcon />}
                                                  onClick={() => idAluno && onDistribuirSaldo(idAluno)}
                                                  disabled={loading || !idAluno}
                                                  color='primary'
                                                  size='small'
                                             />
                                             <ActionButton
                                                  title='Transferir Saldo'
                                                  icon={<SwapHorizIcon />}
                                                  onClick={() => idAluno && onTransferirSaldo(idAluno)}
                                                  disabled={loading || !idAluno || saldoDisponivel <= 0}
                                                  color='secondary'
                                                  size='small'
                                             />
                                             <ActionButton
                                                  title='Configurações'
                                                  icon={<SettingsIcon />}
                                                  onClick={() => idAluno && onConfigurar(idAluno)}
                                                  disabled={loading || !idAluno}
                                                  size='small'
                                             />
                                        </Box>
                                   )}
                              </Grid>

                              {/* Limites (se configurados) */}
                              {(dependente.limite_diario || dependente.limite_semanal) && (
                                   <Grid
                                        item
                                        xs={12}
                                   >
                                        <Typography
                                             variant='caption'
                                             color='text.secondary'
                                             display='block'
                                             sx={{ fontSize: actualMobile ? '0.7rem' : '0.75rem', mb: 0.5 }}
                                        >
                                             Limites configurados:
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                             {dependente.limite_diario && (
                                                  <Chip
                                                       size='small'
                                                       label={`Diário: R$ ${dependente.limite_diario.toFixed(2)}`}
                                                       variant='outlined'
                                                       color='info'
                                                       sx={{
                                                            fontSize: actualMobile ? '0.65rem' : '0.75rem',
                                                            height: actualMobile ? 20 : 24,
                                                       }}
                                                  />
                                             )}
                                             {dependente.limite_semanal && (
                                                  <Chip
                                                       size='small'
                                                       label={`Semanal: R$ ${dependente.limite_semanal.toFixed(2)}`}
                                                       variant='outlined'
                                                       color='info'
                                                       sx={{
                                                            fontSize: actualMobile ? '0.65rem' : '0.75rem',
                                                            height: actualMobile ? 20 : 24,
                                                       }}
                                                  />
                                             )}
                                        </Box>
                                   </Grid>
                              )}
                         </Grid>
                    </Box>
               </CardContent>
          </Card>
     );
};

export default DependenteCard;
